import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { complicityApi } from "../../../modules/complicity/api";
import { ComplicityFilterPageRequest, CreateUpdateComplicity } from "../../../modules/complicity/types";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const COMPLICITY_QUERY_KEYS = {
  filterComplicities: (payload?: ComplicityFilterPageRequest) =>
    payload ? ["filterComplicities", payload] : ["filterComplicities"],
  createComplicity: () => ["createComplicity"],
  updateComplicity: () => ["updateComplicity"],
  deleteComplicity: () => ["deleteComplicity"],
};

export const useFilterComplicities = (payload: ComplicityFilterPageRequest) => {
  return useQuery({
    queryKey: COMPLICITY_QUERY_KEYS.filterComplicities(payload),
    queryFn: async () => {
      const { data } = await complicityApi.filterComplicities(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateComplicity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMPLICITY_QUERY_KEYS.createComplicity(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateComplicity) => {
      const { data } = await complicityApi.createComplicity(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMPLICITIES);
      queryClient.invalidateQueries({ queryKey: COMPLICITY_QUERY_KEYS.filterComplicities() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateComplicity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMPLICITY_QUERY_KEYS.updateComplicity(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateComplicity>) => {
      const { data } = await complicityApi.updateComplicity(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMPLICITIES);
      queryClient.invalidateQueries({ queryKey: COMPLICITY_QUERY_KEYS.filterComplicities() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteComplicity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMPLICITY_QUERY_KEYS.deleteComplicity(),
    mutationFn: (id: UUID) => {
      return complicityApi.deleteComplicity({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.COMPLICITIES);
      queryClient.invalidateQueries({ queryKey: COMPLICITY_QUERY_KEYS.filterComplicities() });
      Notification.itemDeletedNotification();
    },
  });
};
